import React, { useState, useEffect } from 'react';
import {
  Card, Upload, Button, Select, Typography, Alert,
  Table, Tag, Row, Col, Divider, Steps
} from 'antd';
import {
  UploadOutlined, FileExcelOutlined, CheckCircleOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import axios from 'axios';
import * as XLSX from 'xlsx';

const { Title, Text } = Typography;
const API = 'https://q-pmc.com/api/v1';

const getHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('access_token')}`
});

const ExcelImportPage: React.FC = () => {
  const [projects, setProjects] = useState<any[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [importType, setImportType] = useState<string>('activities');
  const [file, setFile] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<any>(null);

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const res = await axios.get(`${API}/projects/`, { headers: getHeaders() });
      setProjects(res.data);
      if (res.data.length > 0) setSelectedProject(res.data[0].id);
    } catch {}
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select an Excel file first.');
      return;
    }
    if (!selectedProject && importType !== 'issue_types' && importType !== 'checklist') {
      setError('Please select a project.');
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('import_type', importType);
      if (selectedProject) formData.append('project_id', selectedProject);

      const res = await axios.post(`${API}/excel/import/`, formData, {
        headers: {
          ...getHeaders(),
          'Content-Type': 'multipart/form-data',
        }
      });
      setResult(res.data);
      setFile(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Import failed. Please check your file format.');
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = (type: string) => {
    const templates: { [key: string]: string[][] } = {
      activities: [
        ['Code', 'Name', 'Discipline'],
        ['CIV-001', 'Site clearing and grubbing', 'CIVIL'],
        ['ELE-001', 'String cable laying', 'ELECTRICAL'],
        ['MNT-001', 'Erection of mounting structure', 'STRUCTURAL'],
      ],
      issue_types: [
        ['Code', 'Name', 'Applicable To (NCR/DELAY/SAFETY)'],
        ['NCR-CIV-001', 'Incorrect foundation dimensions', 'NCR'],
        ['DLY-MAN-001', 'Manpower shortfall', 'DELAY'],
        ['SAF-PPE-001', 'PPE non-compliance', 'SAFETY'],
      ],
      hierarchy: [
        ['Level (1/2/3)', 'Code', 'Name', 'Parent Code'],
        ['1', 'BLK-A', 'Block A - 100MW North', ''],
        ['2', 'BLK-A-Z01', 'Zone 01', 'BLK-A'],
        ['3', 'ROW-001', 'Row 001', 'BLK-A-Z01'],
      ],
      checklist: [
        ['Module Type', 'Parent Name', 'Item Name', 'Expected Criteria', 'Severity Tag (optional)'],
        ['MIR', '', 'Reinforcement Steel', '', ''],
        ['MIR', 'Reinforcement Steel', 'Batch or Test Certificate', 'Must match supplier COA', ''],
        ['MIR', 'Reinforcement Steel', 'Physical Damage Check', 'No visible corrosion; no bends or kinks', ''],
      ],
    };

    const data = templates[type] || [];
    const worksheet = XLSX.utils.aoa_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Template');
    XLSX.writeFile(workbook, `${type}_template.xlsx`);
  };

  const importTypes = [
    { value: 'activities', label: 'Activities Master' },
    { value: 'issue_types', label: 'Issue Types / NCR Categories' },
    { value: 'hierarchy', label: 'Project Hierarchy (Blocks, Zones, Rows)' },
    { value: 'checklist', label: 'Checklist / Observation Tree (MIR, WIR, etc.)' },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={4} style={{ color: '#1B2A4A', marginBottom: 8 }}>
        <FileExcelOutlined /> Excel Import — Bulk Master Data Upload
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
        Upload Excel/CSV files to bulk-load master data. Download the template first to ensure correct format.
      </Text>

      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} closable onClose={() => setError('')} />}

      <Row gutter={24}>
        <Col span={14}>
          <Card title="Upload File" style={{ borderRadius: 12 }}>
            <div style={{ marginBottom: 16 }}>
              <Text strong>1. Select Import Type:</Text>
              <Select
                value={importType}
                onChange={setImportType}
                style={{ width: '100%', marginTop: 8 }}
                options={importTypes}
              />
            </div>

            {importType !== 'issue_types' && importType !== 'checklist' && (
              <div style={{ marginBottom: 16 }}>
                <Text strong>2. Select Project:</Text>
                <Select
                  value={selectedProject}
                  onChange={setSelectedProject}
                  style={{ width: '100%', marginTop: 8 }}
                  options={projects.map(p => ({ value: p.id, label: `${p.code} — ${p.name}` }))}
                />
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <Text strong>{importType !== 'issue_types' ? '3.' : '2.'} Download Template:</Text>
              <br />
              <Button
                icon={<DownloadOutlined />}
                onClick={() => downloadTemplate(importType)}
                style={{ marginTop: 8 }}
              >
                Download {importTypes.find(t => t.value === importType)?.label} Template
              </Button>
            </div>

            <div style={{ marginBottom: 16 }}>
              <Text strong>{importType !== 'issue_types' ? '4.' : '3.'} Select File (.xlsx or .csv):</Text>
              <div style={{ marginTop: 8 }}>
                <input
                  type="file"
                  accept=".xlsx,.csv"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  style={{ marginBottom: 8 }}
                />
                {file && <Tag color="blue">{file.name}</Tag>}
              </div>
            </div>

            <Button
              type="primary"
              icon={<UploadOutlined />}
              onClick={handleUpload}
              loading={loading}
              disabled={!file}
              style={{ background: '#1B2A4A', borderColor: '#1B2A4A' }}
              size="large"
            >
              Import Data
            </Button>
          </Card>
        </Col>

        <Col span={10}>
          <Card title="Import Results" style={{ borderRadius: 12 }}>
            {!result && !loading && (
              <Text type="secondary">Upload a file to see results here.</Text>
            )}

            {result && (
              <div>
                <Alert
                  message={`Import Complete — ${result.import_type}`}
                  type="success"
                  showIcon
                  icon={<CheckCircleOutlined />}
                  style={{ marginBottom: 16 }}
                />
                <Row gutter={16} style={{ marginBottom: 16 }}>
                  <Col span={12}>
                    <Card size="small" style={{ textAlign: 'center', background: '#E8F5E9' }}>
                      <Text style={{ fontSize: 24, fontWeight: 'bold', color: '#1A6B3A' }}>
                        {result.created}
                      </Text>
                      <br />
                      <Text type="secondary">Records Created</Text>
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card size="small" style={{ textAlign: 'center', background: '#FFF8E1' }}>
                      <Text style={{ fontSize: 24, fontWeight: 'bold', color: '#B8860B' }}>
                        {result.skipped}
                      </Text>
                      <br />
                      <Text type="secondary">Skipped (Duplicates)</Text>
                    </Card>
                  </Col>
                </Row>

                {result.errors && result.errors.length > 0 && (
                  <div>
                    <Text strong style={{ color: '#ff4d4f' }}>Errors:</Text>
                    {result.errors.map((err: string, i: number) => (
                      <Alert key={i} message={err} type="error" style={{ marginTop: 4 }} />
                    ))}
                  </div>
                )}
              </div>
            )}
          </Card>

          <Card title="Format Guide" style={{ borderRadius: 12, marginTop: 16 }}>
            <Text strong>Activities:</Text>
            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
              Columns: Code | Name | Discipline
            </Text>
            <Text strong>Issue Types:</Text>
            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
              Columns: Code | Name | Applicable To
            </Text>
            <Text strong>Hierarchy:</Text>
            <Text type="secondary" style={{ display: 'block' }}>
              Columns: Level | Code | Name | Parent Code
            </Text>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ExcelImportPage;